/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Page from '../../../components/Page';
import adminServices from '../../../services/adminServices';

const COLORS = ['#6366f1','#ec4899','#14b8a6','#f59e0b','#8b5cf6','#06b6d4','#ef4444','#22c55e','#f97316','#3b82f6'];
const VIEW_MODES = [
  { key: 'universe', label: 'Universe', icon: '◉' },
  { key: 'er', label: 'ER Diagram', icon: '⬡' },
  { key: 'list', label: 'Table List', icon: '☰' },
];

// Radial layout — most-connected tables in center, others in concentric rings
// ── Universe View (3D Globe) ──────────────────────────────────────────────────
function UniverseView({ tables, relationships, hiddenTables, onSelectTable, selectedTable, highlightConnected }) {
  const containerRef = useRef(null);
  const [rotation, setRotation] = useState({ x: -15, y: 0 });
  const [autoRotate, setAutoRotate] = useState(true);
  const [zoom, setZoom] = useState(1);
  const isDragging = useRef(false);
  const lastMouse = useRef({ x: 0, y: 0 });
  const animRef = useRef(null);

  const visibleTables = useMemo(() => tables.filter(t => !hiddenTables.has(t.name)), [tables, hiddenTables]);
  const visibleRels = useMemo(() => relationships.filter(r => !hiddenTables.has(r.fromTable) && !hiddenTables.has(r.toTable)), [relationships, hiddenTables]);

  // Compute 3D sphere positions
  const positions3D = useMemo(() => {
    const pos = {};
    const n = visibleTables.length;
    if (!n) return pos;
    // Fibonacci sphere distribution for even spacing
    const golden = (1 + Math.sqrt(5)) / 2;
    const radius = 280;
    visibleTables.forEach((t, i) => {
      const theta = Math.acos(1 - 2 * (i + 0.5) / n);
      const phi = 2 * Math.PI * i / golden;
      pos[t.name] = {
        x: radius * Math.sin(theta) * Math.cos(phi),
        y: radius * Math.sin(theta) * Math.sin(phi),
        z: radius * Math.cos(theta),
      };
    });
    return pos;
  }, [visibleTables]);

  const connCount = useMemo(() => {
    const c = {};
    visibleTables.forEach(t => { c[t.name] = 0; });
    visibleRels.forEach(r => { if (c[r.fromTable] !== undefined) c[r.fromTable]++; if (c[r.toTable] !== undefined) c[r.toTable]++; });
    return c;
  }, [visibleTables, visibleRels]);

  const connectedTables = useMemo(() => {
    if (!highlightConnected || !selectedTable) return new Set();
    const s = new Set([selectedTable]);
    visibleRels.forEach(r => { if (r.fromTable === selectedTable) s.add(r.toTable); if (r.toTable === selectedTable) s.add(r.fromTable); });
    return s;
  }, [selectedTable, visibleRels, highlightConnected]);

  // Auto-rotate
  useEffect(() => {
    if (!autoRotate) { if (animRef.current) cancelAnimationFrame(animRef.current); return; }
    const animate = () => {
      setRotation(r => ({ ...r, y: r.y + 0.15 }));
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [autoRotate]);

  const handleMouseDown = (e) => { isDragging.current = true; lastMouse.current = { x: e.clientX, y: e.clientY }; setAutoRotate(false); };
  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    const dx = e.clientX - lastMouse.current.x, dy = e.clientY - lastMouse.current.y;
    setRotation(r => ({ x: Math.max(-80, Math.min(80, r.x - dy * 0.3)), y: r.y + dx * 0.3 }));
    lastMouse.current = { x: e.clientX, y: e.clientY };
  };
  const handleMouseUp = () => { isDragging.current = false; };
  const handleWheel = (e) => { e.preventDefault(); setZoom(z => Math.max(0.5, Math.min(2.5, z + (e.deltaY > 0 ? -0.1 : 0.1)))); };

  // Project 3D to 2D with rotation
  const project = (p3d) => {
    const radX = rotation.x * Math.PI / 180, radY = rotation.y * Math.PI / 180;
    // Rotate Y
    let x = p3d.x * Math.cos(radY) - p3d.z * Math.sin(radY);
    let z = p3d.x * Math.sin(radY) + p3d.z * Math.cos(radY);
    let y = p3d.y;
    // Rotate X
    const y2 = y * Math.cos(radX) - z * Math.sin(radX);
    const z2 = y * Math.sin(radX) + z * Math.cos(radX);
    // Perspective projection
    const perspective = 800;
    const scale = perspective / (perspective + z2);
    return { x: x * scale, y: y2 * scale, z: z2, scale };
  };

  // Sort by Z for proper layering
  const projected = useMemo(() => {
    return visibleTables.map(t => {
      const p3d = positions3D[t.name];
      if (!p3d) return null;
      const p = project(p3d);
      return { ...t, px: p.x, py: p.y, pz: p.z, scale: p.scale };
    }).filter(Boolean).sort((a, b) => a.pz - b.pz);
  }, [visibleTables, positions3D, rotation]);

  return (
    <div ref={containerRef} className="relative w-full h-full overflow-hidden rounded-xl border border-slate-700 select-none"
      style={{ background: 'radial-gradient(ellipse at 50% 50%, #1a1145 0%, #0c0828 35%, #020010 100%)' }}
      onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} onWheel={handleWheel}>

      {/* Stars background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 80 }, (_, i) => (
          <div key={i} className="absolute w-px h-px bg-white rounded-full" style={{ left: `${(i * 37) % 100}%`, top: `${(i * 53) % 100}%`, opacity: 0.1 + (i % 5) * 0.1 }} />
        ))}
      </div>

      {/* Globe wireframe rings */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-10">
        <ellipse cx="50%" cy="50%" rx={140 * zoom} ry={140 * zoom * Math.cos(rotation.x * Math.PI / 180)} fill="none" stroke="#6366f1" strokeWidth="0.5" />
        <ellipse cx="50%" cy="50%" rx={200 * zoom} ry={200 * zoom * Math.cos(rotation.x * Math.PI / 180)} fill="none" stroke="#6366f1" strokeWidth="0.5" />
        <ellipse cx="50%" cy="50%" rx={280 * zoom} ry={280 * zoom * Math.cos(rotation.x * Math.PI / 180)} fill="none" stroke="#6366f1" strokeWidth="0.3" />
      </svg>

      {/* Controls */}
      <div className="absolute top-3 right-3 z-20 flex gap-1">
        <button onClick={() => setAutoRotate(p => !p)} className={`px-2 h-7 rounded text-xs font-medium border ${autoRotate ? 'bg-indigo-600 border-indigo-500 text-white' : 'bg-slate-800/80 border-slate-600 text-white hover:bg-slate-700'}`}>{autoRotate ? '⏸ Pause' : '▶ Rotate'}</button>
        <button onClick={() => setZoom(z => Math.min(2.5, z + 0.3))} className="w-7 h-7 bg-slate-800/80 border border-slate-600 rounded text-sm font-bold text-white hover:bg-slate-700">+</button>
        <button onClick={() => setZoom(z => Math.max(0.5, z - 0.3))} className="w-7 h-7 bg-slate-800/80 border border-slate-600 rounded text-sm font-bold text-white hover:bg-slate-700">−</button>
        <button onClick={() => { setRotation({ x: -15, y: 0 }); setZoom(1); setAutoRotate(true); }} className="px-2 h-7 bg-slate-800/80 border border-slate-600 rounded text-xs font-medium text-white hover:bg-slate-700">Reset</button>
      </div>
      <div className="absolute top-3 left-3 z-20 text-[10px] text-slate-400 bg-slate-900/60 px-2 py-1 rounded">
        {visibleTables.length} tables · {visibleRels.length} connections · Drag to rotate
      </div>

      {/* 3D scene */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div style={{ transform: `scale(${zoom})`, position: 'relative', width: 0, height: 0 }}>
          {/* Connection lines (SVG) */}
          <svg className="absolute pointer-events-none" style={{ left: -600, top: -400, width: 1200, height: 800, overflow: 'visible' }}>
            {visibleRels.map((r, i) => {
              const fromP = positions3D[r.fromTable], toP = positions3D[r.toTable];
              if (!fromP || !toP) return null;
              const from2d = project(fromP), to2d = project(toP);
              const isHL = connectedTables.size > 0 && connectedTables.has(r.fromTable) && connectedTables.has(r.toTable);
              const opacity = connectedTables.size > 0 ? (isHL ? 0.8 : 0.05) : 0.25;
              return <line key={i} x1={from2d.x + 600} y1={from2d.y + 400} x2={to2d.x + 600} y2={to2d.y + 400} stroke={COLORS[i % COLORS.length]} strokeWidth={isHL ? 2 : 0.8} opacity={opacity} />;
            })}
          </svg>

          {/* Table nodes */}
          {projected.map((t, idx) => {
            const isSelected = selectedTable === t.name;
            const isConnected = connectedTables.has(t.name);
            const dimmed = connectedTables.size > 0 && !isConnected;
            const color = COLORS[visibleTables.findIndex(vt => vt.name === t.name) % COLORS.length];
            const size = Math.max(20, Math.min(44, 24 + (connCount[t.name] || 0) * 4));
            const nodeOpacity = dimmed ? 0.1 : (0.4 + t.scale * 0.6);

            return (
              <div key={t.name} className="absolute cursor-pointer group" style={{
                left: t.px - size / 2, top: t.py - size / 2, width: size, height: size,
                opacity: nodeOpacity, zIndex: Math.round(t.scale * 100),
                transform: `scale(${t.scale})`, transition: 'opacity 0.2s',
              }} onClick={(e) => { e.stopPropagation(); onSelectTable(t.name === selectedTable ? null : t.name); }}>
                {/* Hover tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1.5 bg-slate-900 border border-slate-600 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                  <div className="flex items-center gap-1.5">
                    <p className="text-[11px] font-bold text-white">{t.name}</p>
                    <span className="text-[8px] px-1 py-0.5 rounded-full font-bold" style={{ background: getTableGroup(t.name).color + '30', color: getTableGroup(t.name).color }}>{getTableGroup(t.name).label}</span>
                  </div>
                  <p className="text-[9px] text-slate-400">{t.columns?.length} columns · {t.rowEstimate || 0} rows · {connCount[t.name] || 0} links</p>
                </div>
                <div className="w-full h-full rounded-full flex items-center justify-center border"
                  style={{
                    background: `radial-gradient(circle at 35% 35%, ${color}90, ${color}40 60%, ${color}10)`,
                    borderColor: isSelected ? '#fff' : `${color}60`,
                    boxShadow: isSelected ? `0 0 24px ${color}, 0 0 8px #fff` : `0 0 ${(connCount[t.name] || 0) * 3}px ${color}40`,
                  }}>
                  <span className="text-[8px] font-bold text-white text-center leading-tight truncate px-0.5" style={{ maxWidth: size - 6 }}>{t.name}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected table label */}
      {selectedTable && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 px-4 py-2 bg-slate-900/90 border border-slate-600 rounded-xl">
          <p className="text-sm font-bold text-white text-center">{selectedTable}</p>
          <p className="text-[10px] text-slate-400 text-center">{connCount[selectedTable] || 0} connections · Click again to deselect</p>
        </div>
      )}
    </div>
  );
}

// ── ER Diagram View (Traditional box layout with FK lines) ────────────────────
function ERDiagramView({ tables, relationships, hiddenTables, onSelectTable, selectedTable }) {
  const containerRef = useRef(null);
  const [positions, setPositions] = useState({});
  const [dragging, setDragging] = useState(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(0.85);
  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0 });

  const visibleTables = useMemo(() => tables.filter(t => !hiddenTables.has(t.name)), [tables, hiddenTables]);
  const visibleRels = useMemo(() => relationships.filter(r => !hiddenTables.has(r.fromTable) && !hiddenTables.has(r.toTable)), [relationships, hiddenTables]);

  useEffect(() => {
    const cols = Math.ceil(Math.sqrt(visibleTables.length));
    const pos = {};
    visibleTables.forEach((t, i) => { pos[t.name] = { x: (i % cols) * 300 + 30, y: Math.floor(i / cols) * 260 + 30 }; });
    setPositions(pos);
  }, [visibleTables]);

  const handleMouseDown = (e, name) => { e.stopPropagation(); setDragging(name); setOffset({ x: e.clientX - (positions[name]?.x || 0) * zoom - pan.x, y: e.clientY - (positions[name]?.y || 0) * zoom - pan.y }); };
  const handleMouseMove = (e) => {
    if (dragging) setPositions(p => ({ ...p, [dragging]: { x: (e.clientX - offset.x - pan.x) / zoom, y: (e.clientY - offset.y - pan.y) / zoom } }));
    else if (isPanning.current) setPan({ x: e.clientX - panStart.current.x, y: e.clientY - panStart.current.y });
  };
  const handleMouseUp = () => { setDragging(null); isPanning.current = false; };
  const handleBgDown = (e) => { if (e.target === containerRef.current) { isPanning.current = true; panStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y }; } };
  const handleWheel = (e) => { e.preventDefault(); setZoom(z => Math.max(0.3, Math.min(2, z + (e.deltaY > 0 ? -0.08 : 0.08)))); };

  return (
    <div ref={containerRef} className="relative w-full h-full overflow-hidden rounded-xl border border-slate-200 bg-white"
      onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseDown={handleBgDown} onWheel={handleWheel}>
      {/* Controls */}
      <div className="absolute top-3 right-3 z-10 flex gap-1">
        <button onClick={() => setZoom(z => Math.min(2, z + 0.2))} className="w-7 h-7 bg-white border border-slate-200 rounded text-sm font-bold hover:bg-slate-50">+</button>
        <button onClick={() => setZoom(z => Math.max(0.3, z - 0.2))} className="w-7 h-7 bg-white border border-slate-200 rounded text-sm font-bold hover:bg-slate-50">−</button>
        <button onClick={() => { setPan({ x: 0, y: 0 }); setZoom(0.85); }} className="px-2 h-7 bg-white border border-slate-200 rounded text-xs font-medium hover:bg-slate-50">Reset</button>
      </div>

      {/* SVG Lines */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: '0 0' }}>
        {visibleRels.map((r, i) => {
          const from = positions[r.fromTable]; const to = positions[r.toTable];
          if (!from || !to) return null;
          const x1 = from.x + 130, y1 = from.y + 20, x2 = to.x + 130, y2 = to.y + 20;
          const mx = (x1 + x2) / 2;
          return (
            <g key={i}>
              <path d={`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`} fill="none" stroke={COLORS[i % COLORS.length]} strokeWidth={1.5} opacity={0.6} />
              <circle cx={x2} cy={y2} r={4} fill={COLORS[i % COLORS.length]} opacity={0.8} />
              <text x={mx} y={(y1 + y2) / 2 - 6} fontSize={9} fill="#94a3b8" textAnchor="middle">{r.fromColumn}→{r.toColumn}</text>
            </g>
          );
        })}
      </svg>

      {/* Table boxes */}
      <div style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: '0 0', position: 'absolute', inset: 0 }}>
        {visibleTables.map((t, idx) => {
          const pos = positions[t.name]; if (!pos) return null;
          const isSelected = selectedTable === t.name;
          return (
            <div key={t.name} className={`absolute cursor-grab active:cursor-grabbing select-none ${isSelected ? 'ring-2 ring-indigo-500 z-10' : ''}`}
              style={{ left: pos.x, top: pos.y, width: 260 }}
              onMouseDown={(e) => handleMouseDown(e, t.name)} onClick={(e) => { e.stopPropagation(); onSelectTable(t.name); }}>
              <div className="rounded-lg shadow border border-slate-200 bg-white overflow-hidden">
                <div className="px-3 py-2 font-bold text-xs text-white flex items-center justify-between" style={{ background: COLORS[idx % COLORS.length] }}>
                  <span className="truncate">{t.name}</span>
                  <span className="text-[9px] opacity-80">{t.rowEstimate || 0} rows</span>
                </div>
                <div className="divide-y divide-slate-50 max-h-40 overflow-y-auto">
                  {(t.columns || []).map(c => {
                    const isPK = t.constraints?.some(cn => cn.type === 'PRIMARY KEY' && cn.columns?.includes(c.name));
                    const isFK = visibleRels.some(r => r.fromTable === t.name && r.fromColumn === c.name);
                    return (
                      <div key={c.name} className="flex items-center gap-1.5 px-3 py-1 text-[11px]">
                        {isPK && <span className="text-amber-500 text-[9px] font-bold">PK</span>}
                        {isFK && <span className="text-indigo-500 text-[9px] font-bold">FK</span>}
                        {!isPK && !isFK && <span className="w-4" />}
                        <span className="text-slate-700 flex-1 truncate">{c.name}</span>
                        <span className="text-slate-400 text-[10px]">{c.type}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Table List View ───────────────────────────────────────────────────────────
function TableListView({ tables, relationships, hiddenTables, onSelectTable, selectedTable }) {
  const visibleTables = tables.filter(t => !hiddenTables.has(t.name));
  return (
    <div className="w-full h-full overflow-y-auto bg-white rounded-xl border border-slate-200 p-4">
      <div className="grid gap-3">
        {visibleTables.map((t, idx) => {
          const rels = relationships.filter(r => r.fromTable === t.name || r.toTable === t.name);
          const isSelected = selectedTable === t.name;
          return (
            <div key={t.name} onClick={() => onSelectTable(t.name)}
              className={`p-3 rounded-lg border cursor-pointer transition-all ${isSelected ? 'border-indigo-400 bg-indigo-50 shadow' : 'border-slate-200 hover:border-indigo-200 hover:bg-slate-50'}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ background: COLORS[idx % COLORS.length] }} />
                  <span className="text-sm font-bold text-slate-800">{t.name}</span>
                </div>
                <div className="flex gap-2 text-[10px]">
                  <span className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-500">{t.columns?.length} cols</span>
                  <span className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-500">{t.rowEstimate || 0} rows</span>
                  {rels.length > 0 && <span className="px-1.5 py-0.5 bg-indigo-100 rounded text-indigo-600">{rels.length} rels</span>}
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                {(t.columns || []).slice(0, 8).map(c => (
                  <span key={c.name} className="text-[10px] px-1.5 py-0.5 bg-slate-100 rounded text-slate-600">{c.name}</span>
                ))}
                {(t.columns?.length || 0) > 8 && <span className="text-[10px] px-1.5 py-0.5 text-slate-400">+{t.columns.length - 8}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Table Detail Panel ────────────────────────────────────────────────────────
// Auto-detect table group based on name patterns
function getTableGroup(name) {
  const n = name.toLowerCase();
  if (n.includes('auth_user') || n.includes('role_lk') || n.includes('status_lk') || n.includes('session')) return { label: 'Auth', color: '#ef4444' };
  if (n.includes('ebook') || n.includes('chapter') || n.includes('topic')) return { label: 'E-Book', color: '#8b5cf6' };
  if (n.includes('course') || n.includes('module') || n.includes('simulation')) return { label: 'Course', color: '#3b82f6' };
  if (n.includes('subscription') || n.includes('plan') || n.includes('license') || n.includes('billing')) return { label: 'Subscription', color: '#f59e0b' };
  if (n.includes('assessment') || n.includes('question') || n.includes('answer') || n.includes('result')) return { label: 'Assessment', color: '#14b8a6' };
  if (n.includes('client') || n.includes('tenant') || n.includes('department') || n.includes('team')) return { label: 'Organization', color: '#ec4899' };
  if (n.includes('file') || n.includes('media') || n.includes('image') || n.includes('upload')) return { label: 'Media', color: '#06b6d4' };
  if (n.includes('audit') || n.includes('log') || n.includes('notification') || n.includes('announcement')) return { label: 'System', color: '#64748b' };
  if (n.includes('_lk') || n.includes('lookup')) return { label: 'Lookup', color: '#a855f7' };
  return { label: 'Other', color: '#94a3b8' };
}

function generateCreateSQL(table, schema) {
  const cols = (table.columns || []).map(c => {
    let def = `  ${c.name} ${c.type}${c.maxLength ? `(${c.maxLength})` : ''}`;
    if (!c.nullable) def += ' NOT NULL';
    if (c.default) def += ` DEFAULT ${c.default}`;
    return def;
  });
  const pk = (table.constraints || []).find(c => c.type === 'PRIMARY KEY');
  if (pk) cols.push(`  PRIMARY KEY (${pk.columns})`);
  return `CREATE TABLE "${schema}".${table.name} (\n${cols.join(',\n')}\n);`;
}

function TableDetail({ table, relationships, schema }) {
  const [tab, setTab] = useState('columns');
  const [copied, setCopied] = useState(false);
  if (!table) return <div className="flex items-center justify-center h-full text-sm text-slate-400">Select a table to view details</div>;
  const inFK = relationships.filter(r => r.toTable === table.name);
  const outFK = relationships.filter(r => r.fromTable === table.name);
  const group = getTableGroup(table.name);
  const ddl = generateCreateSQL(table, schema);

  const copyDDL = () => { navigator.clipboard?.writeText(ddl); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-slate-200">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-sm font-bold text-slate-800">{table.name}</h3>
          <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: group.color + '20', color: group.color }}>{group.label}</span>
        </div>
        <p className="text-xs text-slate-400">{table.rowEstimate || 0} rows · {table.columns?.length} columns · {table.indexes?.length || 0} indexes</p>
      </div>
      <div className="flex border-b border-slate-100 overflow-x-auto">
        {['columns', 'indexes', 'constraints', 'relations', 'ddl'].map(t => (
          <button key={t} onClick={() => setTab(t)} className={`flex-1 py-2 text-[11px] font-medium border-b-2 whitespace-nowrap px-1 ${tab === t ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>
            {t === 'ddl' ? 'DDL' : t[0].toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-3 text-xs">
        {tab === 'columns' && (
          <table className="w-full">
            <thead><tr className="text-slate-400 border-b"><th className="text-left py-1.5">Name</th><th className="text-left py-1.5">Type</th><th className="text-left py-1.5">Null</th><th className="text-left py-1.5">Default</th></tr></thead>
            <tbody>{(table.columns || []).map(c => (
              <tr key={c.name} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="py-1.5 font-medium text-slate-700">{c.name}</td>
                <td className="py-1.5 text-slate-500">{c.type}{c.maxLength ? `(${c.maxLength})` : ''}</td>
                <td className="py-1.5">{c.nullable ? <span className="text-amber-500">✓</span> : '—'}</td>
                <td className="py-1.5 text-slate-400 truncate max-w-[80px]">{c.default || '—'}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
        {tab === 'indexes' && <div className="space-y-2">
          {(table.indexes || []).length === 0 && <p className="text-slate-400">No indexes</p>}
          {(table.indexes || []).map(i => <div key={i.name} className="p-2 bg-slate-50 rounded border border-slate-100"><p className="font-medium text-slate-700">{i.name}</p><p className="text-[10px] text-slate-400 mt-0.5 font-mono break-all">{i.definition}</p></div>)}
        </div>}
        {tab === 'constraints' && <div className="space-y-2">
          {(table.constraints || []).length === 0 && <p className="text-slate-400">No constraints</p>}
          {(table.constraints || []).map(c => (
            <div key={c.name} className="flex items-center gap-2 p-2 bg-slate-50 rounded border border-slate-100">
              <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${c.type === 'PRIMARY KEY' ? 'bg-amber-100 text-amber-700' : c.type === 'FOREIGN KEY' ? 'bg-indigo-100 text-indigo-700' : 'bg-teal-100 text-teal-700'}`}>{c.type}</span>
              <div><p className="text-slate-700">{c.name}</p>{c.columns && <p className="text-[10px] text-slate-400">{c.columns}</p>}</div>
            </div>
          ))}
        </div>}
        {tab === 'relations' && <div className="space-y-2">
          {outFK.length > 0 && <><p className="text-[9px] font-bold text-slate-400 uppercase mb-1">Outgoing</p>{outFK.map(r => <div key={r.name} className="p-2 bg-indigo-50 rounded mb-1"><span className="font-medium text-indigo-700">{r.fromColumn}</span><span className="text-slate-400"> → </span><span className="font-medium text-indigo-700">{r.toTable}.{r.toColumn}</span></div>)}</>}
          {inFK.length > 0 && <><p className="text-[9px] font-bold text-slate-400 uppercase mb-1 mt-2">Incoming</p>{inFK.map(r => <div key={r.name} className="p-2 bg-green-50 rounded mb-1"><span className="font-medium text-green-700">{r.fromTable}.{r.fromColumn}</span><span className="text-slate-400"> → </span><span className="font-medium text-green-700">{r.toColumn}</span></div>)}</>}
          {outFK.length === 0 && inFK.length === 0 && <p className="text-slate-400">No relationships</p>}
        </div>}
        {tab === 'ddl' && <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[9px] font-bold text-slate-400 uppercase">CREATE TABLE Query</p>
            <button onClick={copyDDL} className={`px-2 py-1 rounded text-[10px] font-medium ${copied ? 'bg-green-100 text-green-700' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`}>
              {copied ? '✓ Copied!' : 'Copy SQL'}
            </button>
          </div>
          <pre className="p-3 bg-slate-900 text-green-400 rounded-lg text-[10px] font-mono whitespace-pre-wrap overflow-x-auto leading-relaxed">{ddl}</pre>
        </div>}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function DatabaseVisualizer() {
  const [schema, setSchema] = useState('public');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTable, setSelectedTable] = useState(null);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('universe');
  const [hiddenTables, setHiddenTables] = useState(new Set());
  const [showTablePanel, setShowTablePanel] = useState(false);
  const [highlightConnected, setHighlightConnected] = useState(true);
  const [schemas, setSchemas] = useState(['public']);

  const fetchSchema = async (s) => {
    setLoading(true);
    const res = await adminServices.getDbSchema(s);
    if (res.ok) {
      setData(res.data);
      // Extract available schemas from table names
      if (s === 'public') {
        const clientSchemas = (res.data.tables || [])
          .filter(t => t.name === 'client')
          .length > 0 ? ['public'] : ['public'];
        setSchemas(prev => [...new Set([...prev, s])]);
      }
    }
    setLoading(false);
  };

  useEffect(() => { fetchSchema(schema); }, [schema]);

  const filteredTables = useMemo(() => (data?.tables || []).filter(t => t.name.toLowerCase().includes(search.toLowerCase())), [data, search]);
  const selectedTableData = data?.tables?.find(t => t.name === selectedTable);

  const toggleHidden = (name) => setHiddenTables(prev => { const s = new Set(prev); s.has(name) ? s.delete(name) : s.add(name); return s; });
  const showAll = () => setHiddenTables(new Set());
  const hideAll = () => setHiddenTables(new Set(filteredTables.map(t => t.name)));
  const showOnlyConnected = () => {
    const connected = new Set();
    (data?.relationships || []).forEach(r => { connected.add(r.fromTable); connected.add(r.toTable); });
    setHiddenTables(new Set(filteredTables.filter(t => !connected.has(t.name)).map(t => t.name)));
  };

  return (
    <Page title="Database Visualizer">
      <div className="flex flex-col bg-gray-50" style={{ height: '100vh', overflow: 'hidden' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white flex-shrink-0">
          <div>
            <h1 className="text-base font-bold text-slate-900">Database Schema Visualizer</h1>
            <p className="text-xs text-slate-400">Interactive database structure & relationships</p>
          </div>
          <div className="flex items-center gap-2">
            {/* View mode toggle */}
            <div className="flex bg-slate-100 rounded-lg p-0.5">
              {VIEW_MODES.map(m => (
                <button key={m.key} onClick={() => setViewMode(m.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${viewMode === m.key ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                  <span className="mr-1">{m.icon}</span>{m.label}
                </button>
              ))}
            </div>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Filter tables..." className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 w-40 focus:outline-none focus:ring-2 focus:ring-indigo-400" />
            <select value={schema} onChange={e => { setSchema(e.target.value); setSelectedTable(null); setHiddenTables(new Set()); }} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-400">
              <option value="public">public</option>
              <option value="tenant_penfield">tenant_penfield</option>
              <option value="tenant_kssfullcreator">tenant_kssfullcreator</option>
            </select>
            <button onClick={() => setShowTablePanel(p => !p)} className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${showTablePanel ? 'bg-indigo-50 border-indigo-300 text-indigo-600' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
              Show/Hide Tables
            </button>
          </div>
        </div>

        {/* Analytics bar */}
        {data?.analytics && (
          <div className="flex items-center gap-4 px-5 py-2 border-b border-slate-100 bg-white flex-shrink-0">
            {[
              { label: 'Tables', value: data.analytics.tableCount, color: '#6366f1' },
              { label: 'Columns', value: data.analytics.columnCount, color: '#ec4899' },
              { label: 'Indexes', value: data.analytics.indexCount, color: '#f59e0b' },
              { label: 'Size', value: data.analytics.totalSize || '—', color: '#14b8a6' },
              { label: 'Relations', value: data.relationships?.length || 0, color: '#8b5cf6' },
              { label: 'Hidden', value: hiddenTables.size, color: '#64748b' },
            ].map(c => (
              <div key={c.label} className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ background: c.color }} />
                <span className="text-xs text-slate-500">{c.label}:</span>
                <span className="text-xs font-bold text-slate-700">{c.value}</span>
              </div>
            ))}
            <div className="ml-auto flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer">
                <input type="checkbox" checked={highlightConnected} onChange={e => setHighlightConnected(e.target.checked)} className="w-3 h-3 rounded text-indigo-600" />
                Highlight connections
              </label>
            </div>
          </div>
        )}

        {/* Main content */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Show/Hide panel */}
          {showTablePanel && (
            <div className="w-56 flex-shrink-0 border-r border-slate-200 bg-white overflow-y-auto">
              <div className="p-3 border-b border-slate-100 flex gap-1">
                <button onClick={showAll} className="flex-1 text-[10px] px-2 py-1 bg-green-50 text-green-700 rounded font-medium hover:bg-green-100">Show All</button>
                <button onClick={hideAll} className="flex-1 text-[10px] px-2 py-1 bg-red-50 text-red-700 rounded font-medium hover:bg-red-100">Hide All</button>
                <button onClick={showOnlyConnected} className="flex-1 text-[10px] px-2 py-1 bg-indigo-50 text-indigo-700 rounded font-medium hover:bg-indigo-100">Connected</button>
              </div>
              <div className="p-2 space-y-0.5">
                {filteredTables.map((t, i) => (
                  <label key={t.name} className="flex items-center gap-1.5 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
                    <input type="checkbox" checked={!hiddenTables.has(t.name)} onChange={() => toggleHidden(t.name)} className="w-3 h-3 rounded text-indigo-600 flex-shrink-0" />
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: getTableGroup(t.name).color }} />
                    <span className="text-[11px] text-slate-700 truncate flex-1">{t.name}</span>
                    <span className="text-[8px] px-1 rounded font-medium flex-shrink-0" style={{ color: getTableGroup(t.name).color }}>{getTableGroup(t.name).label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Visualization */}
          <div className="flex-1 min-w-0 p-3">
            {loading ? (
              <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" /></div>
            ) : viewMode === 'universe' ? (
              <UniverseView tables={filteredTables} relationships={data?.relationships || []} hiddenTables={hiddenTables} onSelectTable={setSelectedTable} selectedTable={selectedTable} highlightConnected={highlightConnected} />
            ) : viewMode === 'er' ? (
              <ERDiagramView tables={filteredTables} relationships={data?.relationships || []} hiddenTables={hiddenTables} onSelectTable={setSelectedTable} selectedTable={selectedTable} />
            ) : (
              <TableListView tables={filteredTables} relationships={data?.relationships || []} hiddenTables={hiddenTables} onSelectTable={setSelectedTable} selectedTable={selectedTable} />
            )}
          </div>

          {/* Detail panel */}
          {selectedTable && (
            <div className="w-80 flex-shrink-0 border-l border-slate-200 bg-white overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
                <span className="text-xs font-bold text-slate-500 uppercase">Table Details</span>
                <button onClick={() => setSelectedTable(null)} className="text-slate-400 hover:text-slate-600 text-lg leading-none">&times;</button>
              </div>
              <TableDetail table={selectedTableData} relationships={data?.relationships || []} schema={schema} />
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}
